import { useEffect, useRef } from "react";
import { emitMascotEvent } from "@/features/mascot/mascotBus";

/**
 * Landing-page motion system, ported from the Claude Design mockup's DCLogic runtime into a
 * single React effect. All patterns are driven off `data-*` attributes on the markup so the
 * section components stay declarative:
 *
 *   [data-reveal]            — fade + translateY into view (once). Variants:
 *                              data-reveal="grow"  → scaleY(0)→1, data-reveal="growx" → scaleX(0)→1,
 *                              data-reveal="none"   → opacity only.
 *   [data-reveal-group]      — staggers its [data-reveal] children (index * 85ms, capped).
 *   [data-parallax-host]     — pointer parallax container; children with [data-depth] drift.
 *   [data-blob]              — cursor-follow accent blob (lerp) inside the parallax host.
 *   [data-magnetic]          — buttons that lean toward the cursor and spring back.
 *   [data-navlink]           — active-section underline (animates background-size).
 *   [data-morph] / [data-morph-label] — scroll-scrubbed "holding → confirmed" state morph.
 *
 * Everything is gated on prefers-reduced-motion (all motion off, content shown) and pointer
 * effects additionally require a fine pointer. Observers/handlers are cleaned up on unmount.
 */
const EASE = "cubic-bezier(.2,.8,.3,1)";
const SPRING = "cubic-bezier(.22,1,.36,1)";

function hiddenTransform(kind: string): string {
  if (kind === "grow") return "scaleY(0)";
  if (kind === "growx") return "scaleX(0)";
  if (kind === "none") return "none";
  return "translateY(22px)";
}

export function useLandingMotion<T extends HTMLElement>() {
  const rootRef = useRef<T | null>(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const mq = (q: string) => typeof window.matchMedia === "function" && window.matchMedia(q).matches;
    const reduce = mq("(prefers-reduced-motion: reduce)");
    const fine = mq("(pointer: fine)");
    const cleanup: Array<() => void> = [];

    // Signal that JS motion is live. CSS keys off this to pre-hide [data-reveal] elements so
    // they don't flash visible before this effect runs; with reduced motion we leave content
    // shown. Set it synchronously here — the effect runs before the browser's next paint.
    root.setAttribute("data-motion", reduce ? "reduce" : "on");
    cleanup.push(() => root.removeAttribute("data-motion"));

    // ---------- scroll-scrubbed state morph (holding -> confirmed) ----------
    // Unlike the one-shot reveal effects below, this story follows the user's actual scroll
    // position and reverses when they scroll back. CSS variables own the colour interpolation,
    // avoiding the broken border longhands produced by mutating React's inline border shorthand.
    const HOLDING_LABEL = "Holding · 9:32 left";
    const CONFIRMED_LABEL = "Confirmed · Tue 10:00 am";
    const morphs: Array<{
      pill: HTMLElement;
      path: SVGPathElement | null;
      label: HTMLElement;
      holdingWidth: number;
      confirmedWidth: number;
      confirmed: boolean;
    }> = [];

    root.querySelectorAll<HTMLElement>("[data-morph]").forEach((pill) => {
      const label = pill.querySelector<HTMLElement>("[data-morph-label]");
      if (!label) return;
      morphs.push({
        pill,
        path: pill.querySelector<SVGPathElement>("path"),
        label,
        holdingWidth: 0,
        confirmedWidth: 0,
        confirmed: label.textContent === CONFIRMED_LABEL,
      });
    });

    if (morphs.length) {
      let morphRaf = 0;
      let morphAlive = true;
      // The first measurement runs on mount and may already read as confirmed (deep link,
      // restored scroll position). That is not a moment the visitor watched happen, so it must
      // not trigger a celebration — only a genuine crossing after settling does.
      let morphSettled = false;

      const measureMorph = (morph: (typeof morphs)[number]) => {
        const previousText = morph.label.textContent;
        const previousWidth = morph.label.style.width;
        morph.label.style.width = "auto";
        morph.label.textContent = HOLDING_LABEL;
        morph.holdingWidth = morph.label.getBoundingClientRect().width;
        morph.label.textContent = CONFIRMED_LABEL;
        morph.confirmedWidth = morph.label.getBoundingClientRect().width;
        morph.label.textContent = previousText;
        morph.label.style.width = previousWidth;
      };

      const setMorphProgress = (morph: (typeof morphs)[number], rawProgress: number) => {
        const clamped = Math.max(0, Math.min(1, rawProgress));
        const progress = clamped * clamped * (3 - 2 * clamped);
        const holdingWeight = (1 - progress) * 100;
        morph.pill.style.setProperty("--lp-morph-progress", progress.toFixed(4));
        morph.pill.style.setProperty("--lp-morph-holding-weight", `${holdingWeight.toFixed(2)}%`);

        const checkProgress = Math.max(0, Math.min(1, (progress - 0.18) / 0.82));
        if (morph.path) {
          morph.path.style.transition = "none";
          morph.path.style.strokeDashoffset = (20 * (1 - checkProgress)).toFixed(3);
        }

        const confirmed = progress >= 0.5;
        if (confirmed !== morph.confirmed) {
          morph.confirmed = confirmed;
          morph.label.textContent = confirmed ? CONFIRMED_LABEL : HOLDING_LABEL;
          // The hold landing on "Confirmed" is the one moment in the hero where the product
          // demo actually succeeds at something, so the mascot celebrates it — once. Scrubbing
          // back up re-arms it, but the reaction's own priority stops it firing repeatedly.
          if (confirmed && morphSettled) emitMascotEvent("demo-confirmed");
        }

        // The text switches only at the fully transparent midpoint. Its measured width is
        // scrubbed at the same time, so the card expands and contracts with the scroll.
        morph.label.style.opacity = Math.min(1, Math.abs(progress - 0.5) * 2).toFixed(3);
        if (morph.holdingWidth > 0 && morph.confirmedWidth > 0) {
          const width =
            morph.holdingWidth + (morph.confirmedWidth - morph.holdingWidth) * progress;
          morph.label.style.width = `${width.toFixed(2)}px`;
        }
      };

      const applyMorphs = () => {
        morphRaf = 0;
        const viewportHeight = window.innerHeight || 1;
        const startLine = viewportHeight * 0.86;
        const endLine = viewportHeight * 0.58;
        morphs.forEach((morph) => {
          const top = morph.pill.getBoundingClientRect().top;
          setMorphProgress(
            morph,
            reduce ? 1 : (startLine - top) / (startLine - endLine),
          );
        });
      };
      const scheduleMorphs = () => {
        if (!morphRaf) morphRaf = requestAnimationFrame(applyMorphs);
      };
      const measureAndApplyMorphs = () => {
        morphs.forEach(measureMorph);
        applyMorphs();
      };

      measureAndApplyMorphs();
      morphSettled = true;
      if (!reduce) {
        window.addEventListener("scroll", scheduleMorphs, { passive: true });
        window.addEventListener("resize", measureAndApplyMorphs);
      }
      void document.fonts?.ready.then(() => {
        if (morphAlive) measureAndApplyMorphs();
      });

      cleanup.push(() => {
        morphAlive = false;
        window.removeEventListener("scroll", scheduleMorphs);
        window.removeEventListener("resize", measureAndApplyMorphs);
        cancelAnimationFrame(morphRaf);
      });
    }

    // ---------- reveals (one-shot) ----------
    // Elements animate IN as they enter the viewport and then STAY visible — scrolling back up
    // does not reverse them. Each element is unobserved once shown so it never re-hides/replays.
    // Entry is staggered per group.
    const reveals = Array.from(root.querySelectorAll<HTMLElement>("[data-reveal]"));
    if (!reduce) {
      const groupDelay = new WeakMap<HTMLElement, number>();

      const show = (el: HTMLElement, delay: number) => {
        el.style.transition = `opacity .65s ${EASE} ${delay}ms, transform .65s ${EASE} ${delay}ms`;
        el.style.opacity = "1";
        el.style.transform = "none";
      };

      // First, hide everything up front (before the IO can fire), so nothing paints visible.
      // [data-reveal-hold] elements are exempt from the CSS pre-hide (see landing.css) so they
      // are present in the very first painted frame for crawlers and screenshot-based checkers.
      // Hiding them here, from JS, keeps their entrance animation identical for real visitors:
      // this effect runs before the next paint, so nothing flashes visible first.
      reveals.forEach((el) => {
        const kind = el.getAttribute("data-reveal") || "";
        const group = el.closest("[data-reveal-group]");
        let delay = 0;
        if (group) {
          const sibs = Array.from(group.querySelectorAll("[data-reveal]"));
          delay = Math.min(sibs.indexOf(el), 12) * 85;
        }
        groupDelay.set(el, delay);
        el.style.willChange = "opacity, transform";
        el.style.opacity = "0";
        el.style.transform = hiddenTransform(kind);
      });

      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            if (!e.isIntersecting) return;
            const el = e.target as HTMLElement;
            show(el, groupDelay.get(el) ?? 0);
            io.unobserve(el); // one-shot: keep it visible, never reverse on scroll-up
          });
        },
        { threshold: 0.18, rootMargin: "0px 0px -6% 0px" }
      );

      // First-load entrance: above-the-fold elements are already in view, so the IO would flip
      // them visible in the same frame they were hidden — with no committed "hidden" paint to
      // transition from, that reads as an instant snap. To get a real animation, commit the
      // hidden state for one frame, THEN play a staggered reveal for whatever is on-screen, and
      // only start observing afterwards (so the IO drives everything that scrolls in later).
      const inView = (el: HTMLElement) => {
        const r = el.getBoundingClientRect();
        return r.top < (window.innerHeight || 0) && r.bottom > 0;
      };
      const firstLoad = reveals.filter(inView);
      const firstLoadSet = new Set(firstLoad);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          firstLoad.forEach((el, i) => {
            // gentle top-to-bottom cascade for the initial screen
            show(el, (groupDelay.get(el) ?? 0) + Math.min(i, 14) * 60);
          });
          // Observe only what wasn't shown in the first-load cascade; those stay put.
          reveals.filter((el) => !firstLoadSet.has(el)).forEach((el) => io.observe(el));
        });
      });
      cleanup.push(() => io.disconnect());
    }

    // ---------- pointer parallax ----------
    if (fine && !reduce) {
      const host = root.querySelector<HTMLElement>("[data-parallax-host]");
      // Where the pointer is *listened* for, which can be wider than the host it moves.
      // The preview only occupied ~18% of the hero, so the drift felt dead unless the
      // cursor happened to land on the card itself. Geometry still keys off `host`, so the
      // layers move exactly as before -- only the region that can drive them grows.
      const surface = root.querySelector<HTMLElement>("[data-parallax-surface]") ?? host;
      if (host && surface) {
        const layers = Array.from(host.querySelectorAll<HTMLElement>("[data-depth]"));
        let raf = 0;
        const onMove = (e: PointerEvent) => {
          const r = host.getBoundingClientRect();
          const px = e.clientX - (r.left + r.width / 2);
          const py = e.clientY - (r.top + r.height / 2);
          cancelAnimationFrame(raf);
          raf = requestAnimationFrame(() => {
            layers.forEach((l) => {
              const d = parseFloat(l.getAttribute("data-depth") || "0") || 0;
              l.style.transition = "transform .12s linear";
              l.style.transform = `translate3d(${(px * d).toFixed(2)}px, ${(py * d).toFixed(2)}px, 0)`;
            });
          });
        };
        const onLeave = () => {
          layers.forEach((l) => {
            l.style.transition = `transform .6s ${EASE}`;
            l.style.transform = "none";
          });
        };
        surface.addEventListener("pointermove", onMove);
        surface.addEventListener("pointerleave", onLeave);
        cleanup.push(() => {
          surface.removeEventListener("pointermove", onMove);
          surface.removeEventListener("pointerleave", onLeave);
        });
      }
    }

    // ---------- 3D tilt (scroll + pointer), à la kita.ai ----------
    // A [data-tilt] element is wrapped in a [data-tilt-scene] that owns the perspective.
    // Two inputs drive it:
    //   • scroll — as the scene travels up the viewport the panel rotates from tilted-back
    //     (looking "into" the screen) toward flat, then a touch past flat as it exits the top.
    //   • pointer — cursor X adds a left/right rotateY (tilt left when the mouse is left,
    //     right when it's right); cursor Y adds a small rotateX. Pointer eases back on leave.
    {
      const scenes = Array.from(root.querySelectorAll<HTMLElement>("[data-tilt-scene]"));
      scenes.forEach((scene) => {
        const panel = scene.querySelector<HTMLElement>("[data-tilt]");
        if (!panel) return;

        // pointer offsets (deg); zeroed on non-fine pointers / reduced motion
        let pRotX = 0;
        let pRotY = 0;
        let scrollRotX = reduce ? 0 : 8;
        let raf = 0;

        const apply = () => {
          raf = 0;
          panel.style.transform = `rotateX(${(scrollRotX + pRotX).toFixed(2)}deg) rotateY(${pRotY.toFixed(2)}deg)`;
        };
        const schedule = () => {
          if (!raf) raf = requestAnimationFrame(apply);
        };

        // scroll: map the scene's vertical position to a base rotateX.
        // center of viewport → ~0deg (flat); below → tilted back (+); above → slight forward (−).
        const onScroll = () => {
          if (reduce) return;
          const r = scene.getBoundingClientRect();
          const vh = window.innerHeight || 1;
          const center = r.top + r.height / 2;
          // progress: 1 when the scene center is at the bottom of the viewport, 0 at center,
          // negative as it passes above center. Clamp so it doesn't over-rotate off-screen.
          const p = Math.max(-0.6, Math.min(1, (center - vh / 2) / (vh / 2)));
          scrollRotX = p * 9; // up to 9deg tilted back, easing to flat, slight forward past center
          schedule();
        };

        cleanup.push(() => cancelAnimationFrame(raf));

        if (fine && !reduce) {
          const onMove = (e: PointerEvent) => {
            const r = scene.getBoundingClientRect();
            const nx = (e.clientX - (r.left + r.width / 2)) / (r.width / 2);  // -1 … 1
            const ny = (e.clientY - (r.top + r.height / 2)) / (r.height / 2); // -1 … 1
            pRotY = Math.max(-1, Math.min(1, nx)) * 6;   // left/right tilt
            pRotX = Math.max(-1, Math.min(1, ny)) * -4;  // up/down tilt (invert: cursor down → tilt back)
            panel.style.transition = "transform .15s ease-out";
            schedule();
          };
          const onLeave = () => {
            pRotX = 0;
            pRotY = 0;
            panel.style.transition = "transform .6s var(--ease-out-soft)";
            schedule();
          };
          // Same widening as the parallax above: the tilt reads the pointer from the
          // hero-wide surface when one is present, while the angles stay keyed to `scene`.
          const tsurface = root.querySelector<HTMLElement>("[data-parallax-surface]") ?? scene;
          tsurface.addEventListener("pointermove", onMove);
          tsurface.addEventListener("pointerleave", onLeave);
          cleanup.push(() => {
            tsurface.removeEventListener("pointermove", onMove);
            tsurface.removeEventListener("pointerleave", onLeave);
          });
        }

        window.addEventListener("scroll", onScroll, { passive: true });
        onScroll(); // set initial tilt
        cleanup.push(() => window.removeEventListener("scroll", onScroll));
      });
    }

    // ---------- cursor-follow blob ----------
    if (fine && !reduce) {
      const blob = root.querySelector<HTMLElement>("[data-blob]");
      const bhost = root.querySelector<HTMLElement>("[data-parallax-host]");
      if (blob && bhost) {
        const r0 = bhost.getBoundingClientRect();
        let tx = r0.width * 0.72;
        let ty = r0.height * 0.45;
        let cx = tx;
        let cy = ty;
        let alive = true;
        let inView = true;
        let blobRaf = 0;
        const scheduleBlob = () => {
          if (!alive || !inView || document.hidden || blobRaf) return;
          blobRaf = requestAnimationFrame(tick);
        };
        const onMove = (e: PointerEvent) => {
          const r = bhost.getBoundingClientRect();
          tx = e.clientX - r.left;
          ty = e.clientY - r.top;
          scheduleBlob();
        };
        const bsurface = root.querySelector<HTMLElement>("[data-parallax-surface]") ?? bhost;
        bsurface.addEventListener("pointermove", onMove);
        function tick() {
          blobRaf = 0;
          if (!alive || !inView || document.hidden) return;
          cx += (tx - cx) * 0.07;
          cy += (ty - cy) * 0.07;
          blob!.style.transform = `translate3d(${cx.toFixed(1)}px, ${cy.toFixed(1)}px, 0)`;
          // Once both axes are below a sub-pixel delta, further frames produce no visible motion.
          // Sleep until the next pointer event instead of writing the same transform at 60fps.
          if (Math.abs(tx - cx) > .1 || Math.abs(ty - cy) > .1) scheduleBlob();
        }
        const visibility = () => {
          if (document.hidden) {
            cancelAnimationFrame(blobRaf);
            blobRaf = 0;
          } else {
            scheduleBlob();
          }
        };
        const intersection = new IntersectionObserver(([entry]) => {
          inView = entry.isIntersecting;
          if (inView) scheduleBlob();
          else {
            cancelAnimationFrame(blobRaf);
            blobRaf = 0;
          }
        });
        intersection.observe(bhost);
        document.addEventListener("visibilitychange", visibility);
        scheduleBlob();
        cleanup.push(() => {
          bsurface.removeEventListener("pointermove", onMove);
          document.removeEventListener("visibilitychange", visibility);
          intersection.disconnect();
          cancelAnimationFrame(blobRaf);
          alive = false;
        });
      }
    }

    // ---------- magnetic buttons ----------
    if (fine && !reduce) {
      root.querySelectorAll<HTMLElement>("[data-magnetic]").forEach((btn) => {
        let raf = 0;
        const onMove = (e: PointerEvent) => {
          const r = btn.getBoundingClientRect();
          const mx = e.clientX - (r.left + r.width / 2);
          const my = e.clientY - (r.top + r.height / 2);
          cancelAnimationFrame(raf);
          raf = requestAnimationFrame(() => {
            btn.style.transition = "transform .12s linear";
            btn.style.transform = `translate(${(mx * 0.25).toFixed(1)}px, ${(my * 0.25).toFixed(1)}px)`;
          });
        };
        const onLeave = () => {
          btn.style.transition = `transform .45s ${SPRING}`;
          btn.style.transform = "none";
        };
        btn.addEventListener("pointermove", onMove);
        btn.addEventListener("pointerleave", onLeave);
        cleanup.push(() => {
          btn.removeEventListener("pointermove", onMove);
          btn.removeEventListener("pointerleave", onLeave);
        });
      });
    }

    // ---------- active-section nav underline ----------
    // Only same-page anchors participate in scroll-spy. Nav links that point at
    // another page ("/docs/") are not selectors — passing one to querySelector
    // throws a SyntaxError, which previously took down the whole landing page.
    const links = Array.from(root.querySelectorAll<HTMLAnchorElement>("[data-navlink]")).filter(
      (l) => (l.getAttribute("href") || "").startsWith("#")
    );
    const sections = links
      .map((l) => root.querySelector(l.getAttribute("href") || ""))
      .filter((s): s is Element => Boolean(s));
    if (sections.length) {
      const setActive = (id: string) => {
        links.forEach((l) => {
          l.style.backgroundSize = l.getAttribute("href") === "#" + id ? "100% 2px" : "0% 2px";
        });
      };
      const spy = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            if (e.isIntersecting) setActive((e.target as HTMLElement).id);
          });
        },
        { rootMargin: "-45% 0px -50% 0px", threshold: 0 }
      );
      sections.forEach((s) => spy.observe(s));
      cleanup.push(() => spy.disconnect());
    }

    return () => {
      cleanup.forEach((fn) => {
        try {
          fn();
        } catch {
          /* ignore */
        }
      });
    };
  }, []);

  return rootRef;
}

/**
 * Drives the pricing billing-cycle toggle: a sliding indicator + a count-up on the price.
 * Kept separate from {@link useLandingMotion} because the pricing section owns its React state
 * (which cycle is selected), and this reads/writes the DOM refs it manages.
 *
 * Returns `animatePrice(el, target)` — a reduced-motion-aware count-up the section calls when
 * the cycle changes or the price first enters view.
 */
export function usePriceCountUp() {
  const reduce =
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  // Writes only the numeric amount into `el` (no currency symbol) — the "$" is rendered
  // separately in the markup so it can use the sans font's cleaner glyph.
  return (el: HTMLElement | null, target: number) => {
    if (!el) return;
    if (reduce) {
      el.textContent = String(target);
      return;
    }
    const from = parseInt((el.textContent || "0").replace(/[^0-9]/g, ""), 10) || 0;
    const dur = 700;
    const start = performance.now();
    const step = (t: number) => {
      let p = Math.min(1, (t - start) / dur);
      p = 1 - Math.pow(1 - p, 3);
      el.textContent = String(Math.round(from + (target - from) * p));
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  };
}
